/**
 * Pict-Provider-TokenAPI
 * ----------------------
 * Browser-side client for the caller's API tokens. Backs the "API Tokens"
 * section in the gear-menu settings panel: list the current user's tokens,
 * mint a new one (the plaintext is returned exactly once), and revoke one.
 *
 * The server scopes every operation to the authenticated IDUser and never
 * returns the token hash, so AppData.APITokens.List holds only safe metadata.
 *
 * AppData shape this provider owns:
 *   AppData.APITokens = {
 *       List:         [],     // [{ IDUserToken, Label, CreateDate, LastUsedAt, ExpiresAt }]
 *       LastLoadedAt: 0,
 *       LoadError:    null,
 *       Loading:      false
 *   }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'TokenAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderTokenAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);

		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.APITokens)
		{
			this.fable.AppData.APITokens =
			{
				List: [],
				LastLoadedAt: 0,
				LoadError: null,
				Loading: false
			};
		}
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	/**
	 * GET /1.0/Tokens -> the caller's own tokens (no hash). Sorted newest first.
	 */
	listTokens()
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.APITokens;
		tmpStore.Loading = true;
		return libFrameworkClient.getJSON(this.fable, this._url('Tokens'))
			.then((pBody) =>
			{
				let tmpRows = (pBody && Array.isArray(pBody.Tokens)) ? pBody.Tokens : [];
				tmpStore.List = tmpRows.slice().sort((pA, pB) => (pB.IDUserToken || 0) - (pA.IDUserToken || 0));
				tmpStore.LastLoadedAt = Date.now();
				tmpStore.LoadError = null;
				tmpStore.Loading = false;
				return tmpStore.List;
			})
			.catch((pErr) =>
			{
				tmpStore.LoadError = pErr.message;
				tmpStore.Loading = false;
				return [];
			});
	}

	/**
	 * POST /1.0/Token { Label } -> { Token (plaintext, ONCE), UserToken (row) }.
	 * Prepends the new row to the local list and resolves with the plaintext so
	 * the caller can show it once. Rejects on failure.
	 */
	createToken(pLabel)
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.APITokens;
		return libFrameworkClient.postJSON(this.fable, this._url('Token'), { Label: pLabel || '' })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.Token)
				{
					throw new Error((pBody && pBody.Error) || 'Could not create token.');
				}
				if (pBody.UserToken)
				{
					tmpStore.List = [pBody.UserToken].concat(tmpStore.List);
				}
				return pBody.Token;
			});
	}

	/**
	 * POST /1.0/Token/For { Email, Label } -> { Token (plaintext, ONCE), ... }. Platform-admin only. Mints a
	 * token for ANOTHER user (e.g. the claude@plansheet.io agent) by account email, so the token belongs to
	 * that user rather than the caller. Resolves with the plaintext so the caller can show it once; rejects on
	 * failure (403 if the caller is not a platform admin, 404 if the email has no user here).
	 */
	mintTokenForUser(pTarget, pLabel)
	{
		// pTarget is an IDUser (from the picker) or an account email; send whichever it is.
		let tmpBody = { Label: pLabel || '' };
		if (typeof pTarget === 'number' || /^\d+$/.test(String(pTarget || ''))) { tmpBody.IDUser = parseInt(pTarget, 10); }
		else { tmpBody.Email = String(pTarget || ''); }
		return libFrameworkClient.postJSON(this.fable, this._url('Token/For'), tmpBody)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.Token)
				{
					throw new Error((pBody && pBody.Error) || 'Could not mint token.');
				}
				return pBody.Token;
			});
	}

	/**
	 * DELETE /1.0/Token/:id -> revoke. Drops the row from the local list.
	 */
	revokeToken(pIDUserToken)
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.APITokens;
		return libFrameworkClient.delJSON(this.fable, this._url('Token/' + pIDUserToken))
			.then(() =>
			{
				tmpStore.List = tmpStore.List.filter((pRow) => pRow.IDUserToken !== pIDUserToken);
				return true;
			})
			.catch((pErr) =>
			{
				tmpStore.LoadError = pErr.message;
				return false;
			});
	}
}

module.exports = PictProviderTokenAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
